import React, { useState, useRef, useEffect, useMemo } from 'react';
import './TooltipV2.css';
import TooltipSvg from './TooltipSvg';

/**
 * TooltipV2 Component
 *
 * A reusable and customizable tooltip component for React applications.
 *
 * Features:
 * - Dynamically adjusts its width and height based on the content.
 * - Supports arrow positioning (left, center, right) with customizable offsets.
 * - Includes accessibility features such as `role="tooltip"` for better screen reader support.
 * - Renders a hidden div to calculate tooltip dimensions for precise positioning.
 *
 * Props:
 * - `children` (ReactNode): The element that triggers the tooltip on hover.
 * - `text` (string): The content to display inside the tooltip.
 * - `arrowPosition` (string): Position of the arrow (`left`, `center`, `right`). Defaults to `center`.
 * - `offset` (number): Custom offset to adjust the tooltip's horizontal position. Defaults to 0.
 */

const MAX_TOOLTIP_WIDTH = 280;
const MIN_TOOLTIP_WIDTH = 130;
const ARROW_SIZE = 20;

const TooltipV2 = ({ children, text, arrowPosition = 'center', offset = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipDimensions, setTooltipDimensions] = useState({
    width: MIN_TOOLTIP_WIDTH,
    height: 40,
  });
  const [arrowX, setArrowX] = useState(40);
  const [tooltipOffset, setTooltipOffset] = useState(0);

  const hiddenRef = useRef(null);

  const arrowFixedPositions = useMemo(
    () => ({
      left: 35,
      center: tooltipDimensions.width / 2 - ARROW_SIZE,
      right: tooltipDimensions.width - 70,
    }),

    [tooltipDimensions.width],
  );

  const tooltipOffsets = useMemo(
    () => ({
      left: tooltipDimensions.width / 4,
      center: 0,
      right: -tooltipDimensions.width / 4,
    }),
    [tooltipDimensions.width],
  );

  const measureHiddenDiv = () => {
    if (!hiddenRef.current) return;

    const { width, height } = hiddenRef.current.getBoundingClientRect();
    const finalWidth = Math.min(
      Math.max(text.length > 30 ? width : MIN_TOOLTIP_WIDTH, MIN_TOOLTIP_WIDTH),
      MAX_TOOLTIP_WIDTH,
    );
    const finalHeight = Math.max(height, 50);

    setTooltipDimensions({ width: finalWidth, height: finalHeight });
  };

  const calculateArrowAndTooltipPositions = () => {
    setArrowX(arrowFixedPositions[arrowPosition] || tooltipDimensions.width / 2 - ARROW_SIZE);
    setTooltipOffset((tooltipOffsets[arrowPosition] || 0) + offset);
  };

  useEffect(() => {
    measureHiddenDiv();
    // This useEffect does not need explicit dependencies
    // because `measureHiddenDiv` is a stable functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    calculateArrowAndTooltipPositions();
    // This useEffect does not need explicit dependencies
    // because `calculateArrowAndTooltipPositions` is a stable functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltipDimensions.width, arrowPosition, offset]);

  return (
    <div className="tooltip-container">
      <div
        className="tooltip-trigger"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>

      {isVisible && (
        <div
          className="tooltip-box"
          style={{
            transform: `translateX(calc(-50% + ${tooltipOffset}px))`,
            width: tooltipDimensions.width,
            height: tooltipDimensions.height + ARROW_SIZE,
          }}
          role="tooltip"
        >
          <TooltipSvg
            width={tooltipDimensions.width}
            height={tooltipDimensions.height}
            arrowX={arrowX}
            arrowSize={ARROW_SIZE}
            text={text}
          />
        </div>
      )}

      <div ref={hiddenRef} className="hidden-div" data-testid="tooltip-hidden">
        {text}
      </div>
    </div>
  );
};

export default TooltipV2;
